import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Key, CreditCard, Code, Shield, Zap, HelpCircle, ChevronDown, ChevronRight, ExternalLink, Layers, Check, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Section({ title, icon: Icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-xl overflow-hidden mb-4 bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors">
        <Icon className="w-5 h-5 text-[#003594] flex-shrink-0" />
        <span className="text-lg font-semibold text-gray-900 flex-1">{title}</span>
        {open ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>
      {open && <div className="px-6 pb-6 border-t">{children}</div>}
    </div>
  );
}

function CodeBlock({ children, lang = 'bash' }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(children));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* no-op */ }
  };
  return (
    <div className="relative my-3 group">
      <button
        onClick={copy}
        data-testid="code-copy-btn"
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-white/10 text-gray-300 hover:bg-white/20 transition-colors"
      >
        {copied ? <><Check className="w-3.5 h-3.5" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
      </button>
      <pre className="bg-[#0d1117] text-[#c9d1d9] rounded-lg p-4 overflow-x-auto text-sm font-mono border border-gray-700">
        <code>{children}</code>
      </pre>
    </div>
  );
}

export default function MerchantGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div style={{ backgroundColor: '#051C2C' }} className="border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Guia de Comercios</h1>
              <p className="text-gray-400 text-sm mt-1">Todo lo que necesitas para integrar Caura Pay en tu negocio</p>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <a href="/documentation" className="inline-flex items-center gap-2 px-4 py-2 bg-[#003594] text-white rounded-lg text-sm font-medium hover:bg-[#002a7a] transition-colors">
              <Code className="w-4 h-4" /> Documentacion API <ExternalLink className="w-3 h-3" />
            </a>
            <a href="/login" className="inline-flex items-center gap-2 px-4 py-2 border border-white/30 text-white rounded-lg text-sm font-medium hover:bg-white/10 transition-colors">
              Iniciar Sesion
            </a>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Quick Overview */}
        <div className="bg-gradient-to-r from-[#003594] to-[#051C2C] rounded-xl p-8 mb-8 text-white">
          <h2 className="text-xl font-bold mb-3">Que es Caura Pay?</h2>
          <p className="text-blue-100 leading-relaxed">
            Caura Pay es un hub de pagos que te permite recibir pagos en Bolivares (VES) a traves de multiples metodos:
            Pago Movil, Debito Inmediato y Transferencias Bancarias. Integras una sola API y nosotros nos encargamos de
            conectar con Banco Plaza, verificar los pagos y dispersar los fondos a tu cuenta.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold">3</p>
              <p className="text-sm text-blue-200">Metodos de Pago</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold">2.5%</p>
              <p className="text-sm text-blue-200">Comision por defecto</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold">24h</p>
              <p className="text-sm text-blue-200">Dispersion de fondos</p>
            </div>
          </div>
        </div>

        <Section title="1. Acceder a tu Panel" icon={Key} defaultOpen={true}>
          <div className="pt-4 space-y-4">
            <p className="text-gray-700 leading-relaxed">
              Cuando tu cuenta es creada por el administrador de Caura Pay, recibiras un email con tus credenciales
              de acceso y API Keys. Usa esos datos para iniciar sesion.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Pasos:</h4>
              <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                <li>Ve a <a href="/login" className="underline font-medium">caurapay.com/login</a></li>
                <li>Ingresa tu email y contrasena proporcionados</li>
                <li>Accederas a tu <strong>Dashboard</strong> donde veras tus proyectos, ordenes y wallets</li>
              </ol>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Importante:</strong> Si necesitas cambiar tu contrasena, contacta al administrador de Caura Pay.
              </p>
            </div>
          </div>
        </Section>

        <Section title="2. Entender tus API Keys" icon={Shield}>
          <div className="pt-4 space-y-4">
            <p className="text-gray-700 leading-relaxed">
              Cada comercio recibe <strong>dos API Keys</strong> al crearse:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 text-xs font-bold uppercase bg-amber-200 text-amber-800 rounded-full">QA / Test</span>
                </div>
                <p className="text-sm text-gray-700 mb-2">Prefijo: <code className="bg-white px-2 py-0.5 rounded text-amber-700 font-mono text-xs">cpt_test_...</code></p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>- Para desarrollo y pruebas</li>
                  <li>- Los pagos se simulan (no se cobran)</li>
                  <li>- Usa esta key para integrar sin riesgo</li>
                </ul>
              </div>
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 text-xs font-bold uppercase bg-green-200 text-green-800 rounded-full">Produccion</span>
                </div>
                <p className="text-sm text-gray-700 mb-2">Prefijo: <code className="bg-white px-2 py-0.5 rounded text-green-700 font-mono text-xs">cpt_live_...</code></p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>- Para cobros reales</li>
                  <li>- Se conecta directamente al banco</li>
                  <li>- Solo usar cuando tu integracion este lista</li>
                </ul>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800"><strong>Nunca compartas</strong> tus API Keys. Son como contrasenas. Si sospechas que fueron comprometidas, contacta al admin.</p>
            </div>
          </div>
        </Section>

        <Section title="3. Crear una Orden de Pago" icon={CreditCard}>
          <div className="pt-4 space-y-4">
            <p className="text-gray-700 leading-relaxed">
              Para cobrar a un cliente, crea una <strong>orden de pago</strong> via API. Esto genera un link de pago
              que puedes enviar al cliente o embeber en tu sitio web.
            </p>
            <h4 className="font-semibold text-gray-900">Endpoint:</h4>
            <CodeBlock>{`POST ${BACKEND_URL}/api/v1/orders
Headers:
  X-API-Key: tu_api_key_aqui
  Content-Type: application/json

Body:
{
  "amount": 5000.00,
  "description": "Compra en Mi Tienda",
  "currency": "VES",
  "order_reference": "PEDIDO-001",
  "customer_email": "cliente@email.com",
  "success_url": "https://mitienda.com/gracias",
  "cancel_url": "https://mitienda.com/cancelado"
}`}</CodeBlock>
            <h4 className="font-semibold text-gray-900">Respuesta:</h4>
            <CodeBlock>{`{
  "order_id": "uuid-de-la-orden",
  "order_code": "CP-ABC123",
  "payment_url": "https://caurapay.com/pay/CP-ABC123",
  "amount": 5000.00,
  "status": "pending",
  "expires_at": "2026-04-02T12:00:00Z"
}`}</CodeBlock>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                <strong>Tip:</strong> Envia el <code>payment_url</code> a tu cliente por WhatsApp, email, o redirigelo desde tu web. El cliente vera los metodos de pago disponibles.
              </p>
            </div>
          </div>
        </Section>

        <Section title="Modo Whitelabel: tu propia pantalla de pago" icon={Layers}>
          <div className="pt-4 space-y-4">
            <p className="text-gray-700 leading-relaxed">
              Tienes <strong>dos formas</strong> de cobrar con Caura Pay:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                <h4 className="font-semibold text-blue-900 mb-2">1. Hosted (mas rapido)</h4>
                <p className="text-sm text-blue-800">Creas la orden y rediriges al cliente al <code>payment_url</code> de Caura Pay. Nosotros mostramos la pantalla de pago.</p>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5">
                <h4 className="font-semibold text-indigo-900 mb-2">2. Whitelabel (tu marca)</h4>
                <p className="text-sm text-indigo-800">Muestras <strong>tu propio formulario</strong> y confirmas el pago via API. El cliente nunca sale de tu sitio.</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Regla de oro:</strong> crea siempre la orden desde <strong>tu backend</strong> con tu API Key (<code>POST /api/v1/orders</code>). Con el <code>order_id</code> que obtienes, tu frontend llama los endpoints <code>/api/public/...</code> (no requieren API Key).
              </p>
            </div>

            <h4 className="font-semibold text-gray-900 mt-2">Pago Movil (P2P) — confirmar el pago</h4>
            <p className="text-sm text-gray-600">Tras crear la orden, el cliente hace el pago movil y tu envias los datos para verificar contra Banco Plaza:</p>
            <CodeBlock>{`POST ${BACKEND_URL}/api/public/orders/{order_id}/verify-payment

Body:
{
  "payment_method": "p2p",       // p2p | c2p | bcv_transfer
  "amount": 500.00,
  "reference": "575987",          // ultimos digitos del pago movil
  "date": "27/08/2025",           // DD/MM/YYYY
  "bankCode": "0102",             // banco del pagador
  "phoneCode": "0424",
  "phone": "1234567",
  "docType": "V",
  "docNumber": "12345678"
}`}</CodeBlock>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Si el banco aun no refleja el pago recibiras <strong>HTTP 202</strong> ("verificando en segundo plano"). El sistema reintenta 1-3 min y te avisa por webhook <code>order.paid</code>. Para transferencia usa <code>payment_method: "bcv_transfer"</code> (sin telefono ni documento).
              </p>
            </div>

            <h4 className="font-semibold text-gray-900 mt-2">Debito Inmediato — token SMS (2 pasos)</h4>
            <p className="text-sm text-gray-600">Primero solicitas el token (llega por SMS al cliente) y luego ejecutas el cobro:</p>
            <CodeBlock>{`// Paso 1: solicitar token
POST ${BACKEND_URL}/api/public/orders/{order_id}/debit/request-token
{
  "identificacion": "V00012345678",  // 12 caracteres
  "banco": "0138",
  "telefono": "04241234567",         // o "cuenta": "0138..."
  "ip": "192.168.1.1"
}

// Paso 2: ejecutar con el token de 8 digitos (valido 2 min)
POST ${BACKEND_URL}/api/public/orders/{order_id}/debit/execute
{ "token": "12345678", "ip": "192.168.1.1" }`}</CodeBlock>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                <code>codigo: "0000"</code> = pago exitoso. <code>codigo: "9999"</code> = enviado al BCV (se confirma en unos minutos por webhook). Cualquier otro codigo = error: muestra el <code>mensaje</code> al cliente.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="guide-nombre-pagador-limit">
              <p className="text-sm text-amber-900">
                <strong>Limite del nombre del pagador:</strong> el banco acepta maximo <strong>35 caracteres alfanumericos</strong> en el
                nombre del pagador (<code>payer_name</code> de la orden). Caura Pay lo normaliza y recorta automaticamente antes de
                enviarlo al banco. La tabla completa de codigos esta en <code>GET {BACKEND_URL}/api/public/debit/response-codes</code> y
                el historial de intentos por orden en <code>GET {BACKEND_URL}/api/v1/orders/{'{order_id}'}/debit-attempts</code>.
              </p>
            </div>

            <h4 className="font-semibold text-gray-900 mt-6">Domiciliación (cargo a cuenta) — cobro contra contrato</h4>
            <p className="text-sm text-gray-600">
              La domiciliación cobra contra un <strong>contrato/mandato ya autorizado</strong> por el deudor.
              <strong> Tú (el comercio) defines el contrato y su fecha al crear la orden</strong>; el <strong>deudor aporta su cuenta y cédula/RIF</strong> en el momento del cobro.
            </p>
            <p className="text-sm text-gray-600 mt-2"><strong>Paso 1 — Crear la orden</strong> con domiciliación habilitada (desde tu backend, con tu API Key):</p>
            <CodeBlock>{`POST ${BACKEND_URL}/api/v1/orders
Headers:
  X-API-Key: tu_api_key_aqui
  Content-Type: application/json

Body:
{
  "amount": 150.00,
  "description": "Suscripcion mensual",
  "currency": "VES",
  "order_reference": "SUB-001",
  "allowed_payment_methods": ["domiciliacion"],
  "domiciliacion_contrato": "202411151239",       // lo defines TU (el comercio)
  "domiciliacion_fecha_contrato": "2024-11-15"     // formato YYYY-MM-DD
}`}</CodeBlock>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                Si habilitas <code>domiciliacion</code> <strong>debes</strong> enviar <code>domiciliacion_contrato</code> y <code>domiciliacion_fecha_contrato</code>, o la API responde <code>HTTP 400</code>.
              </p>
            </div>
            <p className="text-sm text-gray-600 mt-3"><strong>Paso 2 — Cobrar</strong> (tu frontend, con el <code>order_id</code>; el deudor aporta estos datos, sin API Key):</p>
            <CodeBlock>{`POST ${BACKEND_URL}/api/public/orders/{order_id}/domiciliacion/charge

Body:
{
  "identificacion": "E00000722125",       // cedula/RIF del DEUDOR (titular)
  "cuenta": "01380007350070010802",       // cuenta a debitar (20 digitos)
  "nombre": "DA CONCEICAO LUISA"           // nombre del titular
}`}</CodeBlock>
            <p className="text-sm text-gray-600 mt-2">Respuestas posibles:</p>
            <CodeBlock lang="json">{`// Cobro exitoso
{ "success": true, "codigo": "0000", "mensaje": "Cobro procesado exitosamente", "referencia": "..." }

// Pendiente por liquidacion BCV (se confirma solo, via webhook order.paid)
{ "success": false, "pending": true, "codigo": "PE", "mensaje": "Cobro pendiente por liquidacion (BCV)..." }

// Rechazo del banco (muestra el mensaje al cliente)
{ "success": false, "codigo": "RC", "mensaje": "Operacion Rechazada por afiliacion pendiente" }`}</CodeBlock>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Cuando el resultado es <code>PE</code>, Caura Pay <strong>hace seguimiento automático</strong> y marca la orden como pagada al liquidar (evento <code>order.paid</code>). Si una orden lleva <strong>varios días hábiles sin liquidar</strong>, te avisamos por el evento <code>order.domiciliacion.pending_delayed</code>, por email, y en el panel <strong>"Domiciliación en liquidación"</strong> de tu dashboard.
              </p>
            </div>
          </div>
        </Section>

        <Section title="4. Metodos de Pago Disponibles" icon={Zap}>
          <div className="pt-4 space-y-4">
            <p className="text-gray-700 leading-relaxed">Tus clientes pueden pagar con cualquiera de estos metodos:</p>
            <div className="space-y-3">
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-blue-600">P2P</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Pago Movil (P2P)</h4>
                  <p className="text-sm text-gray-600 mt-1">El cliente hace un pago movil desde su banco y envia la referencia. El sistema verifica automaticamente con Banco Plaza.</p>
                  <p className="text-xs text-gray-500 mt-1">Comision bancaria: 0.30% (interbancario) | 0% (mismo banco)</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-purple-600">DI</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Debito Inmediato</h4>
                  <p className="text-sm text-gray-600 mt-1">Se le envia un SMS con un token al cliente. El cliente ingresa el token y el cobro se ejecuta automaticamente desde su cuenta.</p>
                  <p className="text-xs text-gray-500 mt-1">Comision bancaria: 2.00%</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-green-600">BCV</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Transferencia Bancaria (BCV)</h4>
                  <p className="text-sm text-gray-600 mt-1">El cliente hace una transferencia interbancaria a la cuenta de Caura Pay. Se verifica automaticamente contra el BCV.</p>
                  <p className="text-xs text-gray-500 mt-1">Comision bancaria: 0.30%</p>
                </div>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Tasa de Cambio BCV (Endpoint Publico)" icon={Code}>
          <div className="pt-4 space-y-4">
            <p className="text-gray-700 leading-relaxed">
              Consulta la tasa oficial del BCV (USD→VES y EUR→VES) <strong>sin autenticacion</strong>.
              Ideal para mostrar precios en divisas y convertir montos en tu checkout. La tasa se cachea 30 minutos.
            </p>
            <CodeBlock>{`GET ${BACKEND_URL}/api/public/tasa-bcv

// Opcional: ignorar cache y consultar en vivo
GET ${BACKEND_URL}/api/public/tasa-bcv?force_refresh=true`}</CodeBlock>
            <p className="text-sm text-gray-600">Respuesta:</p>
            <CodeBlock lang="json">{`{
  "moneda_base": "USD",
  "moneda_cotizada": "VES",
  "tasa_bcv": 145.30,
  "usd_ves": 145.30,
  "eur_ves": 158.20,
  "fuente": "BCV",
  "fecha": "2026-08-04",
  "actualizado_en": "2026-08-04T12:00:00Z",
  "cache": true,
  "desactualizado": false,
  "disponible": true
}`}</CodeBlock>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                Endpoint publico (no requiere API Key). Si <code>disponible:false</code> o <code>desactualizado:true</code>,
                el BCV no respondio a tiempo — reintenta mas tarde. Usa <code>force_refresh=true</code> con moderacion.
              </p>
            </div>
          </div>
        </Section>

        <Section title="5. Recibir Notificaciones (Webhooks)" icon={Zap}>
          <div className="pt-4 space-y-4">
            <p className="text-gray-700 leading-relaxed">
              Configura un <strong>webhook</strong> para recibir notificaciones automaticas cuando una orden cambie de estado (pagada, cancelada, expirada).
            </p>
            <h4 className="font-semibold text-gray-900">Registrar Webhook:</h4>
            <CodeBlock>{`POST ${BACKEND_URL}/api/v1/webhooks
Headers:
  X-API-Key: tu_api_key_aqui

Body:
{
  "url": "https://mitienda.com/webhook/caurapay",
  "events": ["order.paid", "order.cancelled", "order.expired"]
}`}</CodeBlock>
            <h4 className="font-semibold text-gray-900 mt-4">Payload que recibiras:</h4>
            <CodeBlock>{`{
  "event": "order.paid",
  "webhook_id": "uuid-del-webhook",
  "delivery_id": "uuid-de-la-entrega",
  "timestamp": "2026-10-26T01:49:47.885273+00:00",
  "data": {
    "order_id": "uuid-de-la-orden",
    "order_code": "CP-ABC123",
    "status": "paid",
    "amount": 5000.00,
    "currency": "VES",
    "payment_method": "p2p",
    "payment_reference": "976261111",
    "paid_at": "2026-10-26T01:49:47.402000+00:00"
  }
}`}</CodeBlock>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="webhook-payload-shape-warning">
              <h4 className="font-semibold text-amber-900 mb-2">Los datos van dentro de <code>data</code></h4>
              <p className="text-sm text-amber-800">Lee la orden como <code>body.data.order_id</code>. Si lees <code>body.order_id</code> (en la raiz) obtendras <code>undefined</code>.</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Verificar Firma (Seguridad)</h4>
              <p className="text-sm text-blue-800">Cada entrega incluye los headers <code>X-Caura-Signature</code> (HMAC-SHA256 en hexadecimal), <code>X-Caura-Event</code> y <code>X-Caura-Delivery</code>. Calcula el HMAC sobre los <strong>bytes crudos</strong> del cuerpo usando el <code>secret</code> que recibiste al registrar el webhook: si re-serializas el JSON antes de firmar, la firma no coincidira.</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Reglas de entrega</h4>
              <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                <li>Responde 2xx en menos de 10 segundos y procesa en segundo plano.</li>
                <li>Implementa idempotencia por <code>delivery_id</code> o <code>data.order_id</code>: un evento puede repetirse.</li>
                <li>Reintentos: 7 intentos con backoff (+1m, +5m, +30m, +2h, +12h, +24h).</li>
                <li>Tras 20 fallos consecutivos el webhook se desactiva por circuit breaker.</li>
                <li>No seguimos redirecciones: registra la URL final (sin <code>www.</code> si tu dominio redirige).</li>
              </ul>
            </div>
          </div>
        </Section>

        <Section title="6. Consultar Estado de Ordenes" icon={BookOpen}>
          <div className="pt-4 space-y-4">
            <p className="text-gray-700 leading-relaxed">Puedes consultar el estado de cualquier orden en cualquier momento:</p>
            <CodeBlock>{`GET ${BACKEND_URL}/api/v1/orders/{order_id}
Headers:
  X-API-Key: tu_api_key_aqui`}</CodeBlock>
            <h4 className="font-semibold text-gray-900">Estados posibles:</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <span className="text-xs font-bold text-yellow-800 bg-yellow-200 px-2 py-1 rounded-full">pending</span>
                <p className="text-xs text-gray-600 mt-2">Esperando pago</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="text-xs font-bold text-green-800 bg-green-200 px-2 py-1 rounded-full">paid</span>
                <p className="text-xs text-gray-600 mt-2">Pago confirmado</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="text-xs font-bold text-red-800 bg-red-200 px-2 py-1 rounded-full">cancelled</span>
                <p className="text-xs text-gray-600 mt-2">Cancelada</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-xs font-bold text-gray-800 bg-gray-200 px-2 py-1 rounded-full">expired</span>
                <p className="text-xs text-gray-600 mt-2">Expirada</p>
              </div>
            </div>
          </div>
        </Section>

        <Section title="7. Dispersiones (Recibir tus fondos)" icon={CreditCard}>
          <div className="pt-4 space-y-4">
            <p className="text-gray-700 leading-relaxed">
              Cuando tus clientes pagan, los fondos se acumulan en tu <strong>wallet virtual</strong> dentro de Caura Pay.
              Periodicamente, el sistema dispersa los fondos a tu cuenta bancaria registrada.
            </p>
            <div className="bg-gray-50 rounded-lg p-5 border">
              <h4 className="font-semibold text-gray-900 mb-3">Flujo de fondos:</h4>
              <div className="flex items-center gap-3 text-sm">
                <span className="px-3 py-2 bg-blue-100 text-blue-800 rounded-lg font-medium">Cliente paga</span>
                <span className="text-gray-400">-&gt;</span>
                <span className="px-3 py-2 bg-yellow-100 text-yellow-800 rounded-lg font-medium">Cuenta Operativa</span>
                <span className="text-gray-400">-&gt;</span>
                <span className="px-3 py-2 bg-green-100 text-green-800 rounded-lg font-medium">Tu Wallet</span>
                <span className="text-gray-400">-&gt;</span>
                <span className="px-3 py-2 bg-purple-100 text-purple-800 rounded-lg font-medium">Tu Banco</span>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800"><strong>Nota:</strong> De cada pago se descuenta la comision de la plataforma (configurable, por defecto 2.5%). El saldo restante es lo que se dispersa a tu cuenta.</p>
            </div>
          </div>
        </Section>

        <Section title="8. Preguntas Frecuentes" icon={HelpCircle}>
          <div className="pt-4 space-y-4">
            {[
              { q: 'Cuanto tiempo tarda en verificarse un pago?', a: 'Los pagos P2P y transferencias se verifican en tiempo real (segundos). El Debito Inmediato puede tardar hasta 30 minutos en casos de pendiente BCV.' },
              { q: 'Puedo tener multiples proyectos?', a: 'Si, el admin puede crear multiples proyectos bajo tu cuenta, cada uno con sus propias API Keys y comisiones.' },
              { q: 'Que pasa si mi API Key se compromete?', a: 'Contacta inmediatamente al administrador de Caura Pay para revocar la key y generar una nueva.' },
              { q: 'Puedo probar sin cobrar dinero real?', a: 'Si, usa tu API Key de QA (cpt_test_...). Las transacciones se simulan y no se cobran.' },
              { q: 'Como configuro mi cuenta bancaria para dispersiones?', a: 'El administrador de Caura Pay configura tu cuenta bancaria en el sistema. Contactalo con tus datos bancarios.' },
              { q: 'Hay un limite de monto por transaccion?', a: 'El limite por defecto es VES 1,000,000 por orden. Puede ajustarse segun tu negocio.' },
            ].map((faq, idx) => (
              <div key={idx} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                <h4 className="font-semibold text-gray-900 mb-1">{faq.q}</h4>
                <p className="text-sm text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Footer CTA */}
        <div className="bg-[#051C2C] rounded-xl p-8 text-center mt-8">
          <h3 className="text-xl font-bold text-white mb-2">Listo para integrar?</h3>
          <p className="text-gray-400 mb-6">Consulta la documentacion tecnica completa para empezar a recibir pagos.</p>
          <div className="flex gap-4 justify-center">
            <a href="/documentation" className="px-6 py-3 bg-[#003594] text-white rounded-lg font-medium hover:bg-[#002a7a] transition-colors">Ver Documentacion API</a>
            <a href="/login" className="px-6 py-3 border border-white/30 text-white rounded-lg font-medium hover:bg-white/10 transition-colors">Ir al Panel</a>
          </div>
        </div>
      </div>
    </div>
  );
}
